import type React from "react";
import { useCallback, useState } from "react";
import { toast } from "react-toastify";
import {
  useCreateFocusMatter,
  useFocusMatters,
} from "../../../../hooks/use-focus-matters-api";
import { FocusMatterFormModal } from "./FocusMatterFormModal";

/**
 * ステータスのラベルとバッジクラスを返す
 * @param {string} status ステータス値
 * @return {object} label, badgeClass
 */
function getStatusBadge(status: string): {
  label: string;
  badgeClass: string;
} {
  switch (status) {
    case "open":
      return { label: "募集中", badgeClass: "badge-success" };
    case "filling":
      return { label: "候補あり", badgeClass: "badge-warning" };
    case "filled":
      return { label: "充足", badgeClass: "badge-info" };
    case "closed":
      return { label: "クローズ", badgeClass: "badge-ghost" };
    default:
      return { label: status, badgeClass: "badge-ghost" };
  }
}

/**
 * 注力案件タブコンポーネント
 * 注力案件をカードグリッドで表示する
 * @return {React.ReactElement} 注力案件タブ
 */
export const FocusMatterTab: React.FC = () => {
  const { matters, loading, error, refetch } = useFocusMatters();
  const { createMatter, loading: creating } = useCreateFocusMatter();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleCreate = useCallback(
    async (data: Record<string, unknown>) => {
      try {
        await createMatter(data as never);
        toast.success("注力案件を追加しました");
        setIsModalOpen(false);
        await refetch();
      } catch {
        toast.error("注力案件の追加に失敗しました");
      }
    },
    [createMatter, refetch],
  );

  if (error) {
    return (
      <div className="alert alert-error">
        <span>データの取得に失敗しました: {error.message}</span>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <span className="loading loading-spinner loading-lg" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm text-base-content/60">
          {matters.length}件の注力案件
        </span>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={() => setIsModalOpen(true)}
        >
          注力案件を追加
        </button>
      </div>

      {/* Card grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {matters.map(matter => {
          const statusBadge = getStatusBadge(matter.status);
          return (
            <div key={matter.id} className="card bg-base-100 shadow-sm">
              <div className="card-body p-4">
                <div className="flex items-start justify-between">
                  <h3 className="card-title text-sm">{matter.title}</h3>
                  <span className={`badge badge-sm ${statusBadge.badgeClass}`}>
                    {statusBadge.label}
                  </span>
                </div>

                {matter.clientCompany && (
                  <p className="text-xs text-base-content/60">
                    {matter.clientCompany}
                  </p>
                )}

                {/* Reward range */}
                <div className="text-sm">
                  {matter.rewardMin != null || matter.rewardMax != null ? (
                    <span>
                      {matter.rewardMin != null
                        ? `${matter.rewardMin.toLocaleString()}円`
                        : ""}
                      {matter.rewardMin != null && matter.rewardMax != null
                        ? " ~ "
                        : ""}
                      {matter.rewardMax != null
                        ? `${matter.rewardMax.toLocaleString()}円`
                        : ""}
                    </span>
                  ) : (
                    <span className="text-base-content/60">単価未定</span>
                  )}
                </div>

                {/* Deadline */}
                {matter.deadline && (
                  <div className="text-xs text-base-content/60">
                    期限: {matter.deadline}
                  </div>
                )}

                {/* Required skills */}
                {matter.requiredSkills && matter.requiredSkills.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {matter.requiredSkills.map(skill => (
                      <span
                        key={skill}
                        className="badge badge-outline badge-xs"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                )}

                {/* Candidate count */}
                <div className="mt-2 text-xs text-base-content/60">
                  候補者: {matter.candidateCount}名
                </div>
              </div>
            </div>
          );
        })}

        {matters.length === 0 && (
          <div className="col-span-full text-center text-base-content/60 py-8">
            注力案件がありません
          </div>
        )}
      </div>

      {/* Create modal */}
      <FocusMatterFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleCreate}
        submitting={creating}
      />
    </div>
  );
};
